import { GlobalFunctionsService } from '@_services/global-functions.service';
import { MainAPiServiceService } from '@_services/main-api-service.service';
import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, Subject, Subscription, takeUntil } from 'rxjs';

@Component({
  selector: 'app-add-staff-costings',
  templateUrl: './add-staff-costings.component.html',
  styleUrls: ['./add-staff-costings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddStaffCostingsComponent implements OnInit, AfterViewInit {
  costForm: FormGroup;
  private _unsubscribeAll$: Subject<any> = new Subject();
  confirmDialogRef: MatDialogRef<any>;
  sub: Subscription;
  errorWarningData: any = {};
  errorWarningDataArray: any = {};
  isspiner: boolean = false;
  costFields = [
    { label: 'Annual Salary', key: 'ANNUALSALARY' },
    { label: 'Superannuation', key: 'SUPERANNUATIONAMOUNT' },
    { label: 'Rent', key: 'RENTAMOUNT' },
    { label: 'Computer', key: 'COMPUTERAMOUNT' },
    { label: 'Software', key: 'SOFTWAREAMOUNT' },
    { label: 'Internet', key: 'INTERNETAMOUNT' },
    { label: 'Insurance', key: 'INSURANCEAMOUNT' },
    {
      label: 'Training & Development',
      key: 'TRAININGANDDEVELOPMENTAMOUNT',
    },
    { label: 'Other', key: 'OTHERAMOUNT' },
  ];
  isLoadingResults: boolean;
  Title: string = '';
  FinalAction: string;
  btntext: string;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddStaffCostingsComponent>,
    private _mainAPiServiceService: MainAPiServiceService,
    private toastr: ToastrService,
    private globalFunService: GlobalFunctionsService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private datepipe: DatePipe,
  ) {
    if (this.data?.action == 'new') {
      this.loadDataDefault();
      this.Title = 'Add Cost Details';
      this.FinalAction = 'insert';
      this.btntext = 'Save';
      this.sub = this.costForm?.valueChanges.subscribe((formControl) => {
        this.changeForControl(formControl);
      });
    } else {
      this.Title = 'Update Cost Details';
      this.FinalAction = 'update';
      this.btntext = 'Update';
      this.getSingleUserCosting(this.data?.USERSTAFFCOSTINGGUID);
      this.changeForControl(this.f);
    }
  }

  ngAfterViewInit(): void {
    this.costForm
      .get('ANNUALBILLINGHOURS')
      ?.valueChanges.pipe(debounceTime(500))
      .subscribe(() => {
        this.updateCalculations();
      });
  }

  ngOnInit(): void {
    this.costForm = this.fb.group({
      EFFECTIVEDATE: [''],
      EFFECTIVEDATETEXT: [''],
      ANNUALSALARY: [0],
      SUPERANNUATIONAMOUNT: [0],
      RENTAMOUNT: [0],
      COMPUTERAMOUNT: [0],
      SOFTWAREAMOUNT: [0],
      INTERNETAMOUNT: [0],
      INSURANCEAMOUNT: [0],
      TRAININGANDDEVELOPMENTAMOUNT: [0],
      OTHERAMOUNT: [0],
      TOTALANNUALCOST: [{ value: 0, disabled: true }],
      ANNUALBILLINGHOURS: [0],
      COSTPERBILLINGHOUR: [{ value: 0, disabled: true }],
    });
  }

  loadDataDefault() {
    this.isLoadingResults = true;
    this._mainAPiServiceService
      .getSetData({ Action: 'Default', Data: {} }, 'user-staff-annual-costing')
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(
        (response) => {
          if (response.CODE == 200 && (response.STATUS == 'OK' || response.STATUS == 'success')) {
            const valueObj = response.DATA.DEFAULTVALUES;
            // this.costForm.patchValue(response.DATA.DEFAULTVALUES);
            this.costForm.patchValue(
              {
                ANNUALSALARY: valueObj.ANNUALSALARY.toFixed(2),
                SUPERANNUATIONAMOUNT: valueObj.SUPERANNUATIONAMOUNT.toFixed(2),
                RENTAMOUNT: valueObj.RENTAMOUNT.toFixed(2),
                COMPUTERAMOUNT: valueObj.COMPUTERAMOUNT.toFixed(2),
                SOFTWAREAMOUNT: valueObj.SOFTWAREAMOUNT.toFixed(2),
                INTERNETAMOUNT: valueObj.INTERNETAMOUNT.toFixed(2),
                INSURANCEAMOUNT: valueObj.INSURANCEAMOUNT.toFixed(2),
                TRAININGANDDEVELOPMENTAMOUNT: valueObj.TRAININGANDDEVELOPMENTAMOUNT.toFixed(2),
                OTHERAMOUNT: valueObj.OTHERAMOUNT.toFixed(2),
                ANNUALBILLINGHOURS: valueObj.ANNUALBILLINGHOURS,
              },
              { emitEvent: false },
            );
            this.updateCalculations();
          } else if (response.CODE == 451 && response.STATUS == 'warning') {
            this.isLoadingResults = false;
            this.toastr.warning(response.MESSAGE);
          } else if (response.CODE == 450 && response.STATUS == 'error') {
            this.toastr.error(response.MESSAGE);
            this.isLoadingResults = false;
          } else if (response.MESSAGE == 'Not logged in') {
            this.isLoadingResults = false;
          }
          this.isLoadingResults = false;
        },
        (error) => {
          this.isLoadingResults = false;
          this.toastr.error(error);
        },
      );
  }

  updateCalculations(): void {
    const formValues = this.costForm.getRawValue();
    const totalAnnualCost = this.costFields.reduce((sum, field) => {
      return sum + (parseFloat((formValues[field.key] || '0').toString().replace(/,/g, '')) || 0);
    }, 0);
    const hours = parseFloat(formValues.ANNUALBILLINGHOURS) || 0;
    const costPerHour = hours > 0 ? totalAnnualCost / hours : 0;
    this.costForm.patchValue(
      {
        TOTALANNUALCOST: this.formatIndianNumber(totalAnnualCost),
        COSTPERBILLINGHOUR: this.formatIndianNumber(costPerHour),
      },
      { emitEvent: false },
    );
  }

  formatIndianNumber(value: number): string {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  saveCostDetails(): void {}

  /**
   * Used to get the default protected data.
   */
  getCostDetailsData(): void {
    this.isLoadingResults = true;
    let data = this.costForm.getRawValue();
    let payload = {
      // USERGUID:this.data?.USERGUID,
      // ANNUALBILLINGHOURS:data?.ANNUALBILLINGHOURS,
      // ANNUALSALARY:data.ANNUALSALARY,
      // COMPUTERAMOUNT:data.COMPUTERAMOUNT,
      // COSTPERBILLINGHOUR: Number(data.COSTPERBILLINGHOUR),
      // EFFECTIVEDATE:data.EFFECTIVEDATE,
      // INSURANCEAMOUNT:data.INSURANCEAMOUNT,
      // INTERNETAMOUNT:data.INTERNETAMOUNT,
      // OTHERAMOUNT:data.OTHERAMOUNT,
      // RENTAMOUNT:data.RENTAMOUNT,
      // SOFTWAREAMOUNT:data.SOFTWAREAMOUNT,
      // SUPERANNUATIONAMOUNT:data.SUPERANNUATIONAMOUNT,
      // TOTALANNUALCOST: Number(data.TOTALANNUALCOST),
      // TRAININGANDDEVELOPMENTAMOUNT:data.TRAININGANDDEVELOPMENTAMOUNT,
      // USERSTAFFCOSTINGGUID : (this.btntext == 'Update') ? this.data?.USERSTAFFCOSTINGGUID : ""

      USERGUID: this.data?.USERGUID,
      ANNUALBILLINGHOURS: Number(data?.ANNUALBILLINGHOURS),
      ANNUALSALARY: Number((data?.ANNUALSALARY || '0').toString().replace(/,/g, '')),
      COMPUTERAMOUNT: Number((data?.COMPUTERAMOUNT || '0').toString().replace(/,/g, '')),
      COSTPERBILLINGHOUR: Number((data?.COSTPERBILLINGHOUR || '0').toString().replace(/,/g, '')),
      EFFECTIVEDATE: data?.EFFECTIVEDATE,
      INSURANCEAMOUNT: Number((data?.INSURANCEAMOUNT || '0').toString().replace(/,/g, '')),
      INTERNETAMOUNT: Number((data?.INTERNETAMOUNT || '0').toString().replace(/,/g, '')),
      OTHERAMOUNT: Number((data?.OTHERAMOUNT || '0').toString().replace(/,/g, '')),
      RENTAMOUNT: Number((data?.RENTAMOUNT || '0').toString().replace(/,/g, '')),
      SOFTWAREAMOUNT: Number((data?.SOFTWAREAMOUNT || '0').toString().replace(/,/g, '')),
      SUPERANNUATIONAMOUNT: Number(
        (data?.SUPERANNUATIONAMOUNT || '0').toString().replace(/,/g, ''),
      ),
      TOTALANNUALCOST: Number((data?.TOTALANNUALCOST || '0').toString().replace(/,/g, '')),
      TRAININGANDDEVELOPMENTAMOUNT: Number(
        (data?.TRAININGANDDEVELOPMENTAMOUNT || '0').toString().replace(/,/g, ''),
      ),
      USERSTAFFCOSTINGGUID: this.btntext === 'Update' ? this.data?.USERSTAFFCOSTINGGUID : '',
    };

    if (this.btntext == 'Save') {
      delete payload.USERSTAFFCOSTINGGUID;
    }

    let finalpayload = {
      Action: this.FinalAction,
      VALIDATEONLY: true,
      DATA: payload,
    };

    this._mainAPiServiceService
      .getSetData(finalpayload, 'user-staff-annual-costing')
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(
        (res) => {
          if (res.CODE == 200 && res.STATUS == 'success') {
            this.checkValidation(res.DATA.VALIDATIONS, finalpayload, 'user-staff-annual-costing');
            this.isLoadingResults = false;
          } else if (res.CODE == 451 && res.STATUS == 'warning') {
            this.checkValidation(res.DATA.VALIDATIONS, finalpayload, 'user-staff-annual-costing');
            this.isLoadingResults = false;
          } else if (res.CODE == 450 && res.STATUS == 'error') {
            this.checkValidation(res.DATA.VALIDATIONS, finalpayload, 'user-staff-annual-costing');
            this.isLoadingResults = false;
          } else if (res.MESSAGE == 'Not logged in') {
            this.dialogRef.close(false);
            this.isLoadingResults = false;
          }
        },
        (err) => {
          this.isLoadingResults = false;
          this.toastr.error(err);
        },
      );
  }

  /**
   * Used to check the validation status
   * @param bodyData -Data whitch is come to the API.
   * @param details -Updated data
   * @param APIURL -API end point
   */
  async checkValidation(bodyData: any, details: any, APIURL: any): Promise<void> {
    await this.globalFunService
      .checkValidation(bodyData, details)
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((data) => {
        if (data) {
          this.errorWarningData = data.errorWarningData;
          if (data.callback) {
            this.saveProtectedTrustData(details, APIURL);
          }
        }
      });
  }

  /**
   * Used to save the protected trust data
   * @param data -Data which we need to pass to request body.
   * @param APIURL -API end point.
   */
  saveProtectedTrustData(data: any, APIURL: string): void {
    this.isLoadingResults = true;
    data.VALIDATEONLY = false;
    this._mainAPiServiceService
      .getSetData(data, APIURL)
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(
        (response) => {
          if (response.CODE == 200 && (response.STATUS == 'OK' || response.STATUS == 'success')) {
            //   this.behaviorService.CallProtectedTrustApi$.next(true);
            this.toastr.success('Saved successfully');
            this.dialogRef.close(true);
            this.isLoadingResults = false;
          } else if (response.CODE == 451 && response.STATUS == 'warning') {
            this.toastr.warning(response.MESSAGE);
            this.isLoadingResults = false;
          } else if (response.CODE == 450 && response.STATUS == 'error') {
            this.toastr.error(response.MESSAGE);
            this.isLoadingResults = false;
          } else if (response.MESSAGE == 'Not logged in') {
            this.dialogRef.close(false);
            this.isLoadingResults = false;
          }
        },
        (error) => {
          this.toastr.error(error);
        },
      );
  }

  /**
   * To change the Formcontrol value
   * @param formControl -get updated form control value
   */
  changeForControl(formControl: any): void {
    const dubObj = { ...this.errorWarningDataArray.Error };
    let element = this.errorWarningData.Error;
    for (const [key, value] of Object.entries(dubObj)) {
      const KEY = key.toLocaleUpperCase();
      if (formControl[KEY] != undefined && formControl[KEY] != null && formControl[KEY]) {
        delete element[key];
        dubObj[key] = value;
        this.errorWarningDataArray.Error = { ...dubObj };
      } else if (!formControl[KEY]) {
        element[key] = dubObj[key];
      }
    }

    const dubObj_1 = { ...this.errorWarningDataArray.Warning };
    let element_1 = this.errorWarningData.Warning;
    for (const [key, value] of Object.entries(dubObj_1)) {
      const KEY = key.toLocaleUpperCase();
      if (formControl[KEY] != undefined && formControl[KEY] != null && formControl[KEY]) {
        delete element_1[key];
        dubObj_1[key] = value;
        this.errorWarningDataArray.Warning = { ...dubObj_1 };
      } else if (!formControl[KEY]) {
        element_1[key] = dubObj_1[key];
      }
    }
  }

  /**
   * Used to get the updated data.
   * @param type -
   * @param event -get the updated date
   */
  CommencementDate(type: string, event: MatDatepickerInputEvent<Date>): void {
    this.costForm.controls['EFFECTIVEDATE'].setValue(
      this.datepipe.transform(event.value, 'dd/MM/yyyy'),
    );
  }

  get f() {
    return this.costForm?.controls;
  }

  getSingleUserCosting(getSingleUserCosting) {
    this.isLoadingResults = true;
    this._mainAPiServiceService
      .getSetData(
        {
          Action: 'GetData',
          Filters: { USERSTAFFCOSTINGGUID: getSingleUserCosting },
        },
        'user-staff-annual-costing',
      )
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(
        (response) => {
          if (response.CODE == 200 && (response.STATUS == 'OK' || response.STATUS == 'success')) {
            // this.costForm.patchValue(response.DATA.RECORDS[0]);
            const valueObj = response.DATA.RECORDS[0];
            this.costForm.patchValue(
              {
                ANNUALSALARY: valueObj.ANNUALSALARY.toFixed(2),
                SUPERANNUATIONAMOUNT: valueObj.SUPERANNUATIONAMOUNT.toFixed(2),
                RENTAMOUNT: valueObj.RENTAMOUNT.toFixed(2),
                COMPUTERAMOUNT: valueObj.COMPUTERAMOUNT.toFixed(2),
                SOFTWAREAMOUNT: valueObj.SOFTWAREAMOUNT.toFixed(2),
                INTERNETAMOUNT: valueObj.INTERNETAMOUNT.toFixed(2),
                INSURANCEAMOUNT: valueObj.INSURANCEAMOUNT.toFixed(2),
                TRAININGANDDEVELOPMENTAMOUNT: valueObj.TRAININGANDDEVELOPMENTAMOUNT.toFixed(2),
                OTHERAMOUNT: valueObj.OTHERAMOUNT.toFixed(2),
                ANNUALBILLINGHOURS: valueObj.ANNUALBILLINGHOURS,
                EFFECTIVEDATE: valueObj.EFFECTIVEDATE,
              },
              { emitEvent: false },
            );
            this.updateCalculations();
            let DATE1 = response.DATA.RECORDS[0]['EFFECTIVEDATE'].split('/');
            this.costForm.controls['EFFECTIVEDATETEXT'].setValue(
              new Date(DATE1[1] + '/' + DATE1[0] + '/' + DATE1[2]),
            );
            this.isLoadingResults = false;
          } else if (response.CODE == 451 && response.STATUS == 'warning') {
            this.isLoadingResults = false;
            this.toastr.warning(response.MESSAGE);
          } else if (response.CODE == 450 && response.STATUS == 'error') {
            this.toastr.error(response.MESSAGE);
            this.isLoadingResults = false;
          } else if (response.MESSAGE == 'Not logged in') {
            this.isLoadingResults = false;
          }
          this.isLoadingResults = false;
        },
        (error) => {
          this.isLoadingResults = false;
          this.toastr.error(error);
        },
      );
  }
}
